import React from 'react';
import { motion } from 'framer-motion';
import { X, Check, DollarSign, Users } from 'lucide-react';

export const ProblemSolution = () => {
  const problems = [
    'Lost Orders: Customers give up and go to competitors during peak hours',
    'Staff Costs: Hiring WhatsApp attendants costs $600+/month per person',
    'Mistakes & Complaints: Wrong orders generate refunds and negative reviews',
    'No Scalability: Manual service limits your ability to process more orders'
  ];

  const solutions = [
    'Catch 100% of Orders: AI answers instantly, even at 2 AM',
    '$0 Staff Cost: AI never calls in sick, quits, or needs vacation',
    'Zero Wrong Orders: AI confirms every detail before sending to kitchen',
    'Unlimited Capacity: Handle 10, 50, or 100 simultaneous orders easily'
  ];

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 bg-secondary/5" data-testid="problem-solution-section">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium tracking-wide uppercase text-destructive bg-destructive/10 px-4 py-2 rounded-full inline-block mb-4">
            The Painful Reality of Manual WhatsApp Service
          </span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why Manual Orders Are Killing Your Profit
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Every minute you're flipping burgers while your phone rings is money lost. Here's what's really happening:
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-white p-8 md:p-10 rounded-3xl border-2 border-destructive/20 relative overflow-hidden"
            data-testid="before-card"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-destructive/5 rounded-full -mr-16 -mt-16" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-destructive font-semibold mb-6">
                <X className="w-6 h-6" />
                <span className="text-xl">MANUAL SERVICE</span>
              </div>
              <h3 className="text-2xl font-bold mb-6 text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                The High Cost of "Old Way"
              </h3>
              <ul className="space-y-4">
                {problems.map((problem, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{problem}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-4 bg-destructive/10 rounded-xl">
                <p className="text-sm font-bold text-destructive">💸 Losing $3,000-$5,000/month in missed orders + staff costs</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-gradient-to-br from-accent/30 to-white p-8 md:p-10 rounded-3xl border-2 border-primary/20 relative overflow-hidden shadow-lg"
            data-testid="after-card"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full -mr-16 -mt-16" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-primary font-semibold mb-6">
                <Check className="w-6 h-6" />
                <span className="text-xl">EASYORDER AI</span>
              </div>
              <h3 className="text-2xl font-bold mb-6 text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                The Smart Solution
              </h3>
              <ul className="space-y-4">
                {solutions.map((solution, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-foreground font-medium">{solution}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-4 bg-primary/10 rounded-xl">
                <p className="text-sm font-bold text-primary">💰 Average increase: +$4,200 extra revenue per month</p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center bg-white p-10 rounded-3xl shadow-lg max-w-4xl mx-auto"
        >
          <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Simple Math: <span className="text-primary">ROI From Day One</span>
          </h3>
          <p className="text-muted-foreground mb-8">Stop wasting money on manual order-taking. Start automating and scaling.</p>
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            <div className="p-6 bg-secondary/5 rounded-2xl">
              <DollarSign className="w-10 h-10 text-primary mx-auto mb-3" />
              <p className="text-3xl font-bold text-foreground mb-2">$99/mo</p>
              <p className="text-sm text-muted-foreground">EasyOrder Pro Plan</p>
            </div>
            <div className="p-6 bg-accent/20 rounded-2xl">
              <Users className="w-10 h-10 text-secondary mx-auto mb-3" />
              <p className="text-3xl font-bold text-foreground mb-2">$0</p>
              <p className="text-sm text-muted-foreground">Zero staff salaries or training</p>
            </div>
            <div className="p-6 bg-primary/10 rounded-2xl border-2 border-primary">
              <Check className="w-10 h-10 text-primary mx-auto mb-3" />
              <p className="text-3xl font-bold text-primary mb-2">+$4,200</p>
              <p className="text-sm text-foreground font-medium">Average extra revenue/month</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSolution;